import { datadogRum } from '@datadog/browser-rum';
import {
  getDefaultStore, useAtomValue, useSetAtom,
} from 'jotai';
import { useLocalStorage } from 'usehooks-ts';

import {
  fetchAccount,
  fetchApplyEligibility,
  getLoanOfferRoute,
  refreshTokens,
  toApplyEligibility,
  useSubmitApplication,
  UseSubmitApplicationResultT,

} from '@consumer-frontend/nc-api';
import {
  applicationDecisions,
  isDefined,
  isRecentlyVerified,
  LOAN_AMOUNT_AND_PURPOSE_LOCAL_STORAGE_KEY,
  NcBorrowingNeedsFormT,
  redirectTo,
} from '@consumer-frontend/nc-utils';

import { hasIncome, isApplyEligible } from './checks';
import {
  applyEligibilityAtom,
  doesServerHaveIncomeDataAtom,
  iovationBlackboxAtom,
  iovationPurchasedAtom,
  isReturningCustomerFlowAtom,
} from '../store';

type UseApplyImmediatelyReturnT = {
  applyImmediately: () => Promise<boolean>;
  isSubmitApplicationReady: boolean;
  submitApplication: () => Promise<UseSubmitApplicationResultT>;
};

export function useApplyImmediately(): UseApplyImmediatelyReturnT {
  const isReturningCustomerFlow = useAtomValue(isReturningCustomerFlowAtom);
  const setDoesServerHaveIncomeData = useSetAtom(doesServerHaveIncomeDataAtom);
  const iovationBlackbox = useAtomValue(iovationBlackboxAtom);
  const [loanAmountAndPurpose] = useLocalStorage<Partial<NcBorrowingNeedsFormT>>(
    LOAN_AMOUNT_AND_PURPOSE_LOCAL_STORAGE_KEY,
    {},
  );
  const { loanAmount, loanPurpose } = loanAmountAndPurpose;

  const { submitApplication } = useSubmitApplication(
    {
      consentsRequired: false,
      isReturningCustomerFlow,
      loanAmount,
      loanPurpose,
    },
    iovationBlackboxAtom,
    iovationPurchasedAtom,
  );

  const store = getDefaultStore();

  const applyImmediately = async (): Promise<boolean> => {
    try {
      const account = await fetchAccount();
      const eligibility = await fetchApplyEligibility();
      const accountHasIncome = hasIncome(account);

      if (accountHasIncome) { setDoesServerHaveIncomeData(true); }
      store.set(applyEligibilityAtom, toApplyEligibility(eligibility));

      if (!isReturningCustomerFlow
        || !isApplyEligible()
        || !isRecentlyVerified(account?.last_verified_at)
        || !accountHasIncome) {
        return false;
      }

      const { isIovationPurchaseSuccessful, response } = await submitApplication();
      const decision = response?.decision;

      if (
        !isIovationPurchaseSuccessful
        || response?.status !== 201
        || !isDefined(decision)
        || !applicationDecisions.includes(decision)
      ) {
        return false;
      }

      await refreshTokens();
      redirectTo(getLoanOfferRoute(decision));

      return true;
    } catch (error) {
      datadogRum.addError(error, { context: 'useApplyImmediately' });

      return false;
    }
  };

  const isSubmitApplicationReady = iovationBlackbox.isComplete && !!iovationBlackbox.blackbox;

  return {
    applyImmediately,
    isSubmitApplicationReady,
    submitApplication,
  };
}
